import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { fullWidth, halfWidth } from "../../blocks/layout/columnSpans";

/**
 * Fixture as page config. Start and end are entered and stored as local time; a new
 * start moves the end with it, and the end must come after the start.
 */
export const fixturePageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "dwfixture",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 9, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-fixture-details",
              title: "Fixture details",
              fields: [
                {
                  id: "field-name",
                  type: "text",
                  name: "name",
                  label: "Name",
                  required: true,
                  isNameField: true,
                  span: halfWidth,
                },
                {
                  id: "field-fixturedescription",
                  type: "text",
                  name: "fixturedescription",
                  label: "Description",
                  span: halfWidth,
                },
                {
                  id: "field-fixturestart",
                  type: "datetime",
                  name: "fixturestart",
                  label: "Start",
                  required: true,
                  storedAsLocal: true,
                  onChange: { behaviour: "fixtureEndFromStart", target: "fixtureend" },
                  span: halfWidth,
                },
                {
                  id: "field-fixtureend",
                  type: "datetime",
                  name: "fixtureend",
                  label: "End",
                  storedAsLocal: true,
                  validatorKey: "fixtureEndAfterStart",
                  span: halfWidth,
                },
                {
                  id: "field-opponentid",
                  type: "lookup",
                  name: "opponentid",
                  label: "Opponent",
                  lookupEntity: "dwopponent",
                  searchable: true,
                  required: true,
                  span: halfWidth,
                },
                {
                  id: "field-teamid",
                  type: "lookup",
                  name: "teamid",
                  label: "Team",
                  lookupEntity: "dwteam",
                  searchable: true,
                  required: true,
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-competition",
              title: "Competition",
              fields: [
                {
                  id: "field-competitionid",
                  type: "lookup",
                  name: "competitionid",
                  label: "Competition",
                  lookupEntity: "dwcompetition",
                  searchable: true,
                  required: true,
                  span: halfWidth,
                },
                {
                  id: "field-competitionstageid",
                  type: "lookup",
                  name: "competitionstageid",
                  label: "Competition Stage",
                  lookupEntity: "dwcompetitionstage",
                  searchable: true,
                  span: halfWidth,
                },
                {
                  id: "field-fixturegradeid",
                  type: "lookup",
                  name: "fixturegradeid",
                  label: "Fixture Grade",
                  lookupEntity: "dwfixturegrade",
                  searchable: true,
                  span: halfWidth,
                },
                {
                  id: "field-lastseasonfixtureid",
                  type: "lookup",
                  name: "lastseasonfixtureid",
                  label: "Last Season Fixture",
                  lookupEntity: "dwfixture",
                  searchable: true,
                  optionFilterKey: "lastSeasonFixture",
                  span: halfWidth,
                },
              ],
            },
          ],
        },
        {
          id: "column-secondary",
          span: { desktop: 3, tablet: 3, mobile: 12 },
          card: true,
          fillHeight: true,
          blocks: [
            {
              kind: "section",
              id: "section-classification",
              title: "Classification",
              fields: [
                {
                  id: "field-seasonid",
                  type: "lookup",
                  name: "seasonid",
                  label: "Season",
                  lookupEntity: "dwseason",
                  searchable: true,
                  required: true,
                  span: fullWidth,
                },
                {
                  id: "field-seasonalperiodid",
                  type: "lookup",
                  name: "seasonalperiodid",
                  label: "Seasonal Period",
                  lookupEntity: "dwseasonalperiod",
                  searchable: true,
                  span: fullWidth,
                },
                {
                  id: "field-homestatusid",
                  type: "lookup",
                  name: "homestatusid",
                  label: "Venue Status",
                  lookupEntity: "dwhomestatus",
                  searchable: true,
                  required: true,
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-broadcast",
              title: "Broadcast",
              fields: [
                {
                  id: "field-ontv",
                  type: "boolean",
                  name: "ontv",
                  label: "On TV",
                  span: fullWidth,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "row-automation",
      columns: [
        {
          id: "column-automation",
          span: fullWidth,
          blocks: [
            {
              kind: "tables",
              id: "tables-automation-readiness",
              tabs: [
                {
                  id: "tab-automation-readiness",
                  label: "Automation readiness",
                  // Read with the fixture by GetPageData; saved fixtures only.
                  entityName: "fixtureautomationreadiness",
                  layout: "cards",
                  // Empty on a new fixture, so the section is there from the start.
                  showWhenNew: true,
                  emptyMessage:
                    "No active fixture automations currently apply to this fixture.",
                  columns: [
                    {
                      key: "name",
                      label: "Automation",
                      link: { to: "/automation", idKey: "automationid", newTab: "button" },
                    },
                    {
                      key: "statuslabel",
                      label: "Status",
                      display: "badge",
                      variantKey: "statusvariant",
                    },
                    { key: "fixturename", label: "Fixture", cardSlot: "subtitle" },
                    { key: "modelabel", label: "Mode", cardSlot: "subtitle" },
                    // Formatted by the API as the legacy card showed them, "-" if unset.
                    { key: "processingwindowstartsat", label: "Processing starts" },
                    { key: "dueat", label: "Send" },
                    {
                      key: "communicationname",
                      label: "Communication",
                      link: {
                        to: "/email-communication",
                        idKey: "communicationid",
                        newTab: "button",
                      },
                    },
                    {
                      key: "publishinglabel",
                      label: "Publishing",
                      display: "badge",
                      variantKey: "publishingvariant",
                      cardAttachTo: "communicationname",
                    },
                    {
                      key: "audiencename",
                      label: "Audience",
                      link: { to: "/audience", idKey: "audienceid", newTab: "button" },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
